import { Alert, Button, Flex, Text } from '@servicetitan/anvil2';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { formatCurrency } from '@servicetitan/financing-utils-shared';
import { TextField, toNumber } from '@servicetitan/anvil2-react-hook-form';
import { ProviderSelection } from './ProviderSelection';
import { FinancedAmountStore } from './financedAmountStore';

export interface FormProps {
    onSubmit: SubmitHandler<LoanFormData>;
    onCancel?: () => void;
    showProviderSelection?: boolean;
    primaryButtonText?: string;
    /** Maximum allowed financed amount (e.g., approved loan amount) */
    maxAmount?: number;
}

export interface LoanFormData {
    saleAmount: number;
    downPayment: number;
}

export const Form = ({
    onSubmit,
    onCancel,
    showProviderSelection,
    primaryButtonText = 'Find Plans',
    maxAmount,
}: FormProps) => {
    const { saleAmount: defaultSaleAmount, downPayment: defaultDownPayment } =
        FinancedAmountStore.useStore() ?? { saleAmount: 1000, downPayment: 0 };

    const methods = useForm({
        defaultValues: {
            downPayment: defaultDownPayment,
            saleAmount: defaultSaleAmount,
        },
    });
    const [saleAmount, downPayment] = methods.watch(['saleAmount', 'downPayment']);
    const financedAmount = toNumber(saleAmount) - toNumber(downPayment);
    const exceedsMaxAmount = maxAmount != null && financedAmount > maxAmount;
    const isSubmitDisabled = financedAmount <= 0 || exceedsMaxAmount;

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Flex direction="column" gap="6">
                    <Flex direction="column">
                        <Text variant="headline" el="h2" style={{ margin: 0 }}>
                            Financing Information
                        </Text>
                        <Text>
                            Select a provider and enter the sale information to find available
                            plans.
                        </Text>
                    </Flex>
                    {showProviderSelection && <ProviderSelection />}
                    {exceedsMaxAmount && (
                        <Alert
                            status="warning"
                            title={`Amount cannot exceed ${formatCurrency(maxAmount)}`}
                        />
                    )}
                    <Flex gap="3" direction="column" md={{ direction: 'row' }}>
                        <TextField
                            label="Sale Amount"
                            name="saleAmount"
                            type="number"
                            prefix="$"
                            inputMode="decimal"
                            required
                        />
                        <TextField
                            label="Down Payment"
                            name="downPayment"
                            type="number"
                            prefix="$"
                            inputMode="decimal"
                        />
                    </Flex>
                    <Flex direction="column">
                        <Text>Desired Financed Amount</Text>
                        <Text variant="headline" el="h3" style={{ margin: 0 }}>
                            {formatCurrency(financedAmount)}
                        </Text>
                    </Flex>
                    <Flex gap="4" style={{ width: '100%' }}>
                        <Button
                            appearance="primary"
                            type="submit"
                            disabled={isSubmitDisabled}
                            style={!onCancel ? { width: '100%' } : undefined}
                        >
                            {primaryButtonText}
                        </Button>
                        {onCancel && (
                            <Button appearance="secondary" onClick={onCancel}>
                                Cancel
                            </Button>
                        )}
                    </Flex>
                </Flex>
            </form>
        </FormProvider>
    );
};
